import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Accordion from '@mui/material/Accordion';
import { AccordionSectionDetails, AccordionSectionHeader } from 'src/components/accordion-section';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';

import Responsive from 'src/components/responsive/Responsive';
import Flex from 'src/components/flex/Flex';
import { type EConversationCategory, type IConversationMatchWithChannel } from 'src/pages/landlord/conversations/context/types';
import {
    DesktopConversationListItem,
    MobileConversationListItem,
} from 'src/pages/landlord/conversations/components/list/ConversationListItem';
import { type IConversation } from 'src/api/landlord-messages-api';
import { useProperties, useLoggedInUser, type ILead } from 'src/services/api';
import styles from 'src/pages/landlord/conversations/components/list/FreshnessGroupAccordion.module.css';

interface ISimpleConversationsTableProps {
    conversations: IConversation[] | IConversationMatchWithChannel[];
    /**
     * This is used to facilitate navigation once user switches to messages view.
     */
    navigationConversations: (IConversation | IConversationMatchWithChannel)[];
    leadsByUuid: Map<string, ILead>;
    /**
     * These are used for checkboxes
     */
    selectedConversations?: Array<IConversation | IConversationMatchWithChannel>;
    setSelectedConversations?: (conversations: Array<IConversation | IConversationMatchWithChannel>) => void;
    /**
     * These are used to support keyboard navigation in conversations/inbox view
     */
    focusedConversation?: IConversation | IConversationMatchWithChannel;
    setFocusedConversation?: (conversation: IConversation | undefined) => void;
    variant?: 'default' | 'longPreview';
}

/**
 * This is just very simple presentation
 */
export function SimpleDesktopConversationsTable({
    conversations,
    navigationConversations,
    leadsByUuid,
    selectedConversations,
    setSelectedConversations,
    focusedConversation,
    setFocusedConversation,
    variant = 'default',
}: ISimpleConversationsTableProps) {
    const { data: loggedInUser } = useLoggedInUser();

    if (!loggedInUser) { return null; }

    return <>
        <Box className={styles.scrollContainer}>
            <TableContainer component={Paper} elevation={0} square>
                <Table size="small" className={styles.table} data-testid="conversation-table">
                    <TableBody>
                        {conversations.map((conversation) => {
                            const lead = leadsByUuid?.get(conversation.lead_uuid);

                            return (
                                <DesktopConversationListItem
                                    key={conversation.lead_uuid}
                                    conversation={conversation}
                                    lead={lead}
                                    loggedInUser={loggedInUser!}
                                    navigationConversations={navigationConversations}
                                    leadsByUuid={leadsByUuid!}
                                    selectedConversations={selectedConversations}
                                    setSelectedConversations={setSelectedConversations}
                                    focusedConversation={focusedConversation}
                                    setFocusedConversation={setFocusedConversation}
                                    variant={variant}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    </>;
}

type Props = {
    category: EConversationCategory;
    conversationsByCategory: Record<EConversationCategory, IConversation[]>;
    visibleConversationsByCategory: Record<EConversationCategory, IConversation[]>;
    leadsByUuid: Map<string, ILead>;
    selectedConversations: Array<IConversation | IConversationMatchWithChannel>;
    setSelectedConversations: React.Dispatch<React.SetStateAction<Array<IConversation | IConversationMatchWithChannel>>>;
    focusedConversation?: IConversation | IConversationMatchWithChannel;
    setFocusedConversation: React.Dispatch<React.SetStateAction<IConversation | undefined>>;
    rowsPerPage: number;
    pagesByCategory: Record<EConversationCategory, number>;
    setPagesByCategory: React.Dispatch<React.SetStateAction<Record<EConversationCategory, number>>>;
    setMobileMenuAnchorEl: (anchorEl: HTMLElement | undefined) => void;
}

/**
 * This is one entire freshness category of conversations (e.g. "Unread")
 * It includes pagination, selection, collapsing, and the list of conversations itself.
 */
export default function FreshnessGroupAccordion({
    category,
    conversationsByCategory,
    visibleConversationsByCategory,
    leadsByUuid,
    selectedConversations,
    setSelectedConversations,
    focusedConversation,
    setFocusedConversation,
    rowsPerPage,
    pagesByCategory,
    setPagesByCategory,
    setMobileMenuAnchorEl,
}: Props) {
    const hash = useLocation().hash;

    // Fetch data locally
    const { data: properties } = useProperties();

    const [expanded, setExpanded] = useState(true);

    const page = pagesByCategory[category];
    const conversations = conversationsByCategory[category];
    const visibleConversations = visibleConversationsByCategory[category];

    const pageCount = Math.ceil((conversations?.length || 0) / rowsPerPage);

    const numberOfConversations = conversations.length;
    const hasConversations = !!conversations.length;

    let endIndex = page * rowsPerPage;
    if (endIndex > numberOfConversations) {
        endIndex = numberOfConversations;
    }
    const startIndex = (page - 1) * rowsPerPage + 1;

    const allSelected = hasConversations &&
        visibleConversations.every((conversation) => {
            return selectedConversations.some((
                selectedConversation
            ) => selectedConversation.lead_uuid === conversation.lead_uuid);
        });

    const selectAll = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (allSelected) {
            setSelectedConversations(prev => {
                return prev.filter((conversation) => {
                    return !visibleConversations.some((
                        selectedConversation
                    ) => selectedConversation.lead_uuid === conversation.lead_uuid);
                });
            });
        } else {
            setSelectedConversations(prev => {
                const newSelectedConversations = visibleConversations.filter((conversation) => {
                    return !prev.some((
                        selectedConversation
                    ) => selectedConversation.lead_uuid === conversation.lead_uuid);
                });

                return [...prev, ...newSelectedConversations];
            });
        }
    };

    const setPage = (newPage: number) => {
        setPagesByCategory(prev => ({
            ...prev,
            [category]: newPage
        }));
        setSelectedConversations([]);
        setFocusedConversation?.(undefined);
    };

    useEffect(() => {
        setExpanded(hasConversations);
    }, [hasConversations]);

    // Scroll to the category according to the hash in the URL
    useEffect(() => {
        if (hash) {
            const element = document.querySelector(hash);
            if (element) {
                element.scrollIntoView();
            }
        }
    }, [hash]);

    const propertiesById = useMemo(() => {
        return new Map(properties?.map((o) => [o.id, o]) ?? []);
    }, [properties]);

    return (
        <Accordion
            id={category}
            disableGutters
            square
            elevation={0}
            data-testid={'conversation-section'}
            expanded={expanded}
            onChange={(_, value) => setExpanded(value)}
            className={styles.accordionRoot}
        >
            <AccordionSectionHeader
                expanded={expanded}
                title={category}
                className={clsx(
                    styles.header,
                    hasConversations && styles.headerHasConversations,
                    selectedConversations.length > 0 && styles.headerHasSelection
                )}
                leading={(
                    <Responsive desktop>
                        {/* checkbox table cell width minus left padding and column gap*/}
                        <Box className={styles.checkboxSpacer}>
                            <Checkbox
                                checked={allSelected}
                                onClick={selectAll}
                                className={styles.checkbox}
                            />
                        </Box>
                    </Responsive>
                )}
                trailing={pageCount > 0 ? (
                    <Flex alignItems="center" onClick={event => event.stopPropagation()}>
                        <Typography variant="caption">
                            {startIndex} - {endIndex} of {conversations.length}
                        </Typography>

                        <Flex className={styles.pageControls}>
                            <IconButton onClick={() => setPage(page - 1)} disabled={page === 1} size="small">
                                <ChevronLeftIcon />
                            </IconButton>

                            <IconButton onClick={() => setPage(page + 1)} disabled={page === pageCount} size="small">
                                <ChevronRightIcon />
                            </IconButton>
                        </Flex>
                    </Flex>
                ) : null}
            />

            <Responsive desktop>
                <AccordionSectionDetails className={styles.details} isEmpty={!hasConversations} emptyMessage={`No ${category.toLowerCase()} messages`}>
                    <SimpleDesktopConversationsTable
                        conversations={visibleConversations}
                        navigationConversations={conversationsByCategory[category]}
                        leadsByUuid={leadsByUuid}
                        selectedConversations={selectedConversations}
                        setSelectedConversations={setSelectedConversations}
                        focusedConversation={focusedConversation}
                        setFocusedConversation={setFocusedConversation}
                    />
                </AccordionSectionDetails>
            </Responsive>

            <Responsive mobile>
                <AccordionSectionDetails className={styles.details} isEmpty={!hasConversations} emptyMessage={`No ${category.toLowerCase()} messages`}>
                    {/* NOTE: the 100vw is necessary otherwise everything looks weird */}
                    <Box className={styles.mobileScroll}>
                        {visibleConversations.map((conversation) => {
                            const property = propertiesById.get(conversation.property_id!);

                            return (
                                <MobileConversationListItem
                                    key={conversation.lead_uuid}
                                    conversation={conversation}
                                    property={property}
                                    navigationConversations={conversationsByCategory[category]}
                                    leadsByUuid={leadsByUuid}
                                    setMobileMenuAnchorEl={setMobileMenuAnchorEl}
                                    setSelectedConversations={setSelectedConversations}
                                />
                            );
                        })}
                    </Box>
                </AccordionSectionDetails>
            </Responsive>
        </Accordion>
    );
}
